/**
 * Test data for Intelligence Collection Requests (ICR)
 * Exported values:
 * - ICR_TEST_DATA: Array of ICR objects
 * - getIcrByNumber(icrNumber): helper to find an ICR by its ICR Number (string)
 */

const ICR_TEST_DATA = [
    {
        icrNumber: 'EUCOM-2025-ICR-0143',
        dtg: '311000ZOCT25',
        classification: 'SECRET//REL TO USA, FVEY',
        requestingOrganization: 'USAREUR-AF G-2, Collection Division',
        requestor: 'MAJ Smith, EUCOM JAC Analyst',
        pointOfContact: 'DSN 314-555-4123, smithmj@army.mil',
        priority: 'High',
        subject: 'Assessment of Russian UAV activity near Kaliningrad',
        informationRequirement: 'Determine composition, operational tempo, and basing of Russian UAV units operating within Kaliningrad Oblast (Oct–Nov 2025).',
        supportedDecision: 'Deterrence posture review',
        currentAssessment: 'Last imagery coverage 15 Oct 2025; no recent SIGINT.',
        tags: ['uav', 'kaliningrad', 'russia'],
        eeis: [
            {text: 'What UAV systems (types/models) are deployed and from which bases?'},
            {text: 'Identify unit-level types, serials if available, and support infrastructure'},
        ],
        eei: {
            what: 'What UAV systems (types/models) are deployed and from which bases?',
            where: 'Kaliningrad Oblast, Russia',
            when: 'Past 30 days (relative to TOI)',
            detail: 'Identify unit-level types, serials if available, and support infrastructure'
        },
        backgroundJustification: 'Increased UAV launches observed near Suwałki Gap; EUCOM leadership requests updated assessment to support deterrence posture review.',
        targetInformation: {
            targetNameTypeActivity: 'Russian UAV brigades under WMD District command',
            geographicArea: 'Kaliningrad Oblast, Russia',
            timeOfInterest: '01–30 Nov 2025'
        },
        recommendedCollectionDisciplines: ['IMINT', 'SIGINT', 'OSINT'],
        knownSourcesPreviousReporting: 'Last imagery coverage 15 Oct 2025; no recent SIGINT.',
        constraintsSpecialInstructions: 'Avoid duplication with NATO JISR coverage; report within 72 hours of tasking.',
        classificationCaveats: 'SECRET//REL TO USA, FVEY',
        disseminationControls: 'NOFORN until validated',
        validation: {
            validatedBy: 'LTC J. Ramos, EUCOM Collection Manager (J-2X)',
            position: 'Collection Manager (J-2X)',
            date: '311200ZOCT25'
        },
        collectionManagerNotes: 'Task to NGA for IMINT; NSA for SIGINT correlation; feed results to JAC EUCOM portal.',
        responseFeedback: '[To be completed upon receipt of reporting.]'
    },

    {
        icrNumber: 'PACOM-2025-ICR-0007',
        dtg: '150900NOV25',
        classification: 'CONFIDENTIAL',
        requestingOrganization: 'PACOM J-2, Collection Branch',
        requestor: 'CPT Lee, Analytical Team',
        pointOfContact: 'POC: +1-808-555-0199, lee_c@military.mil',
        priority: 'Low',
        subject: 'Commercial fishing vessels patterns near disputed atoll',
        informationRequirement: 'Assess presence of state-affiliated vessels conducting surveillance disguised as fishing operations.',
        supportedDecision: 'Maritime domain awareness and partner coordination',
        currentAssessment: 'AIS logs through 10 Oct 2025; commercial imagery 05 Oct 2025',
        tags: ['maritime', 'south-china-sea', 'fishing'],
        eeis: [
            {text: 'Identify vessel registration, flag state, and equipment visible (antennas, sensor arrays).'},
            {text: 'Vessel IDs, AIS track correlations, and recommended imagery tasking'},
        ],
        eei: {
            what: 'Identify vessel registration, flag state, and equipment visible (antennas, sensor arrays).',
            where: 'South China Sea — vicinity of Atoll X',
            when: '01–31 Oct 2025',
            detail: 'Vessel IDs, AIS track correlations, and recommended imagery tasking'
        },
        backgroundJustification: 'Unusual aggregation of fishing vessels near contested features; concerns about covert ISR activities.',
        targetInformation: {
            targetNameTypeActivity: 'Commercial fishing fleets; possible maritime militia units',
            geographicArea: 'South China Sea — Atoll X AOI',
            timeOfInterest: '01–31 Oct 2025'
        },
        recommendedCollectionDisciplines: ['IMINT', 'OSINT', 'GEOINT'],
        knownSourcesPreviousReporting: 'AIS logs through 10 Oct 2025; commercial imagery 05 Oct 2025',
        constraintsSpecialInstructions: 'Do not task assets within exclusive economic zone of partner nations without coordination.',
        classificationCaveats: 'CONFIDENTIAL',
        disseminationControls: 'REL TO PACOM',
        validation: {
            validatedBy: 'MAJ Gomez, PACOM Collection Manager',
            position: 'Collection Manager',
            date: '150930NOV25'
        },
        collectionManagerNotes: 'Coordinate with maritime domain awareness cell; request high-res tasking windows at dawn/dusk.',
        responseFeedback: null
    },

    {
        icrNumber: 'AFRICOM-2025-ICR-0021',
        dtg: '050700NOV25',
        classification: 'SECRET',
        requestingOrganization: 'AFRICOM G-2, HUMINT Division',
        requestor: 'SFC Johnson, HUMINT Collector',
        pointOfContact: 'johnson.s@army.mil',
        priority: 'High',
        subject: 'Emerging advertisement of advanced telecom gear to extremist groups',
        informationRequirement: 'Identify procurement networks and origins of telecom equipment being sold to extremist cells.',
        supportedDecision: 'Counter-proliferation and interdiction planning',
        currentAssessment: 'Seizure reports dated 01 Oct 2025; local media',
        tags: ['west-africa', 'procurement', 'telecom'],
        eeis: [
            {text: 'Supplier identities, shipping routes, and financial intermediaries'},
            {text: 'Supplier level (company), container manifests, and shipment serials'},
        ],
        eei: {
            what: 'Supplier identities, shipping routes, and financial intermediaries',
            where: 'West Africa — markets and ports',
            when: 'Ongoing; last 60 days',
            detail: 'Supplier level (company), container manifests, and shipment serials'
        },
        backgroundJustification: 'Recent seizures indicate a new supply chain for high-end comms equipment to illicit networks.',
        targetInformation: {
            targetNameTypeActivity: 'Traders, forwarders, and brokers facilitating illicit procurement',
            geographicArea: 'Selected West African coastal markets',
            timeOfInterest: 'Last 60 days'
        },
        recommendedCollectionDisciplines: ['HUMINT', 'OSINT', 'SIGINT'],
        knownSourcesPreviousReporting: 'Seizure reports dated 01 Oct 2025; local media',
        constraintsSpecialInstructions: 'Protect sources; HUMINT ops require additional oversight.',
        classificationCaveats: 'SECRET',
        disseminationControls: 'NOFORN; ORCON',
        validation: {
            validatedBy: 'CPT A. Mensah, AFRICOM Collection Manager',
            position: 'Collection Manager',
            date: '050730NOV25'
        },
        collectionManagerNotes: 'Prioritize HUMINT collection and corroborate with shipments.',
        responseFeedback: 'Initial reporting indicates two probable supplier routes; follow-on required.'
    },

    {
        icrNumber: 'NORTHCOM-2025-ICR-1001',
        dtg: '010600DEC25',
        classification: 'UNCLASSIFIED',
        requestingOrganization: 'NORTHCOM Civil Support',
        requestor: 'CIV Lead, Emergency Management',
        pointOfContact: 'emergencymanagement@northcom.mil',
        priority: 'Low',
        subject: 'Assessment of critical infrastructure status after major storm',
        informationRequirement: 'Who is affected, what services are down, and expected restoration timelines.',
        supportedDecision: 'Civil support and disaster response coordination',
        currentAssessment: 'Local utility outage feeds; social media reports',
        tags: ['infrastructure', 'disaster-response', 'storm'],
        eeis: [
            {text: 'Status of power grids, water treatment, and transport routes'},
            {text: 'Outage maps, estimated restoration times, and alternate routing'},
        ],
        eei: {
            what: 'Status of power grids, water treatment, and transport routes',
            where: 'Affected state regions',
            when: 'Immediate post-storm 72-hour window',
            detail: 'Outage maps, estimated restoration times, and alternate routing'
        },
        backgroundJustification: 'Severe weather event caused widespread outages; civil authorities requested federal situational awareness.',
        targetInformation: {
            targetNameTypeActivity: 'Critical infrastructure nodes (substations, pumping stations)',
            geographicArea: 'Coastal counties',
            timeOfInterest: 'First 72 hours post-event'
        },
        recommendedCollectionDisciplines: ['OSINT', 'GEOINT'],
        knownSourcesPreviousReporting: 'Local utility outage feeds; social media reports',
        constraintsSpecialInstructions: 'Use only publicly available information for initial reporting.',
        classificationCaveats: 'NONE',
        disseminationControls: 'NORELE',
        validation: {
            validatedBy: 'CIV Lead',
            position: 'Emergency Management Lead',
            date: '010630DEC25'
        },
        collectionManagerNotes: 'Prioritize open-source feeds and liaise with FEMA.',
        responseFeedback: null
    },

    {
        icrNumber: 'NATO-EUCOM-2025-ICR-0210',
        dtg: '071200NOV25',
        classification: 'SECRET',
        requestingOrganization: 'EUCOM J-2, Strategic Assessments',
        requestor: 'LTC Perez, Strategic Analyst',
        pointOfContact: 'perezl@eucom.mil',
        priority: 'Medium',
        subject: 'Russia–North Korea strategic military partnership',
        informationRequirement: 'Assess the scope and nature of military cooperation and material transfers between Russia and North Korea, to include any capabilities or transfers that could affect the conduct of the war in Ukraine and regional stability.',
        supportedDecision: 'Allied defense planning and sanctions enforcement',
        currentAssessment: 'OSINT chatter indicating restricted shipments; satellite tasking requests in the past 60 days.',
        tags: ['dprk', 'russia', 'ukraine'],
        eeis: [
            {text: 'What military items, personnel exchanges, or training arrangements are being transferred or coordinated between Russia and North Korea (include weapons, munitions, ballistic/tactical tech).'},
            {text: 'Itemized list of systems, transport manifests, units involved, dates of transfer, and likely end-users'},
        ],
        eei: {
            what: 'What military items, personnel exchanges, or training arrangements are being transferred or coordinated between Russia and North Korea (include weapons, munitions, ballistic/tactical tech).',
            where: 'Ports, training areas, and transit routes linking DPRK and Russian staging areas; possible transfer nodes in the Russian Far East and DPRK ports.',
            when: 'Last 180 days; focus on recent surge indicators',
            detail: 'Itemized list of systems, transport manifests, units involved, dates of transfer, and likely end-users'
        },
        backgroundJustification: 'Open-source reporting and recent sanction-evasion indicators suggest a potential uptick in Moscow–Pyongyang military coordination; EUCOM requires an assessment to understand implications for the war in Ukraine and allied defenses.',
        targetInformation: {
            targetNameTypeActivity: 'Defense-industrial nodes, transport/logistics nodes, and liaison units enabling Russia–DPRK exchanges',
            geographicArea: 'Russian Far East, DPRK eastern ports, and intermediate transit hubs',
            timeOfInterest: 'Last 180 days'
        },
        recommendedCollectionDisciplines: ['SIGINT', 'IMINT', 'OSINT', 'HUMINT'],
        knownSourcesPreviousReporting: 'OSINT chatter indicating restricted shipments; satellite tasking requests in the past 60 days.',
        constraintsSpecialInstructions: 'Coordinate reporting with allied partners and sanctions enforcement cells; observe legal/us policy caveats.',
        classificationCaveats: 'SECRET//REL TO USA, FVEY',
        disseminationControls: 'NOFORN until validated',
        validation: {
            validatedBy: 'COL S. Anders, EUCOM Collection Manager',
            position: 'Collection Manager (J-2)',
            date: '071230NOV25'
        },
        collectionManagerNotes: 'Task IMINT collection for ports and transport nodes; cross-cue SIGINT for maritime and rail manifests; prioritize corroboration via OSINT and HUMINT.',
        responseFeedback: null
    },

    {
        icrNumber: 'EUCOM-2025-ICR-0250',
        dtg: '071400NOV25',
        classification: 'SECRET',
        requestingOrganization: 'EUCOM J-2, Tactical Collection Branch',
        requestor: 'MAJ Ivanov, Collection Planner',
        pointOfContact: 'ivanov@eucom.mil',
        priority: 'Medium',
        subject: 'Ukrainian drones: operational patterns, capabilities, and sustainment',
        informationRequirement: 'Determine types/models, operational tempo, basing and sustainment chains for Ukrainian drones operating in current conflict zones; identify implications for targeting, defensive vulnerabilities, and cross-border transfer risks.',
        supportedDecision: 'Force protection and targeting decisions',
        currentAssessment: 'Open-source videos/timestamps of drone strikes, maintenance facility imagery, and social media procurement posts.',
        tags: ['ukraine', 'drones', 'loitering-munitions'],
        eeis: [
            {text: 'What drone systems (types/models/payloads) are employed by Ukrainian forces and associated proxies; what are typical mission profiles and payload employment (ISR, strike, loitering munitions)?'},
            {text: 'Unit-level disposition where available, manufacturing/repair sources, sustainment logistics, and evidence of international procurement or transfers'},
        ],
        eei: {
            what: 'What drone systems (types/models/payloads) are employed by Ukrainian forces and associated proxies; what are typical mission profiles and payload employment (ISR, strike, loitering munitions)?',
            where: 'Areas of operations in Ukraine (front-line sectors, logistic hubs, and launch/recovery sites); potential staging and repair nodes in neighboring areas.',
            when: 'Past 90 days with emphasis on recent shifts in employment and tempo',
            detail: 'Unit-level disposition where available, manufacturing/repair sources, sustainment logistics, and evidence of international procurement or transfers'
        },
        backgroundJustification: 'Increased employment of tactical and loitering drones has changed operational dynamics; leadership requires updated characterization to inform force protection and targeting decisions.',
        targetInformation: {
            targetNameTypeActivity: 'Ukrainian drone units, local manufacture/repair facilities, and supply chains',
            geographicArea: 'Selected front-line AOIs in Ukraine and identified logistic/staging nodes',
            timeOfInterest: 'Last 90 days'
        },
        recommendedCollectionDisciplines: ['IMINT', 'SIGINT', 'OSINT', 'HUMINT', 'GEOINT'],
        knownSourcesPreviousReporting: 'Open-source videos/timestamps of drone strikes, maintenance facility imagery, and social media procurement posts.',
        constraintsSpecialInstructions: 'Ensure collection respects partner sovereignty; coordinate with allied ISR to avoid duplication and deconflict signals.',
        classificationCaveats: 'SECRET//REL TO USA, FVEY',
        disseminationControls: 'NOFORN until validated',
        validation: {
            validatedBy: 'LTC M. Novak, EUCOM Collection Manager',
            position: 'Collection Manager (J-2)',
            date: '071430NOV25'
        },
        collectionManagerNotes: 'Prioritize IMINT for launch/recovery nodes and SIGINT for C2 chatter; cross-reference OSINT footage and HUMINT leads for corroboration.',
        responseFeedback: null
    }
];

/**
 * Find an ICR by its ICR Number (case-insensitive match)
 * @param {string} icrNumber - ICR Number to look up (e.g., 'EUCOM-2025-ICR-0143')
 * @returns {object|null} - The matching ICR object or null if not found
 */
function getIcrByNumber(icrNumber) {
    if (!icrNumber || typeof icrNumber !== 'string') return null;
    const key = icrNumber.trim().toLowerCase();
    return ICR_TEST_DATA.find(icr => (icr.icrNumber || '').toLowerCase() === key) || null;
}

module.exports = {
    ICR_TEST_DATA,
    getIcrByNumber
};
